import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import Confetti from 'react-confetti';
import {
    GetOrCreateXfSessionId,
    SaveXfParticipantSession,
    GetStoredXfParticipantSession,
    ClearXfParticipantSession,
    ValidateXfRegistrationForm,
    GetXfPublicConfig,
    ValidateXfTicketToken,
    RegisterXfParticipant,
    RecoverXfPass,
    GetXfDemoSampleTickets
} from '../../utilities/BL/XoloFest';

const CATEGORY_BADGE_STYLES = {
    VIP: {
        bg: 'linear-gradient(135deg, #f59e0b 0%, #b45309 100%)',
        text: '#09090b',
        border: '#fde68a',
        label: 'VIP EXCLUSIVE'
    },
    CANCHA: {
        bg: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
        text: '#ffffff',
        border: '#fca5a5',
        label: 'EXPERIENCIA CANCHA'
    },
    STAFF: {
        bg: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
        text: '#ffffff',
        border: '#93c5fd',
        label: 'STAFF OFICIAL'
    },
    GENERAL: {
        bg: 'linear-gradient(135deg, #27272a 0%, #18181b 100%)',
        text: '#f4f4f5',
        border: '#52525b',
        label: 'ACCESO GENERAL'
    }
};

export const XoloFestLanding = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    const qrTokenParam = searchParams.get('t') || '';
    const refParam = searchParams.get('ref') || '';

    // Dimensiones para Confetti
    const [windowSize, setWindowSize] = useState({
        width: typeof window !== 'undefined' ? window.innerWidth : 400,
        height: typeof window !== 'undefined' ? window.innerHeight : 800
    });

    // Configuración pública y sesión
    const [sessionId] = useState(() => GetOrCreateXfSessionId());
    const [eventConfig, setEventConfig] = useState(null);
    const [activeSession, setActiveSession] = useState(() => GetStoredXfParticipantSession());
    const [showConfetti, setShowConfetti] = useState(false);

    // Estado de validación del QR (?t=...)
    const [validatingToken, setValidatingToken] = useState(false);
    const [qrValidation, setQrValidation] = useState({
        checked: false,
        isValid: false,
        ticketFolio: '',
        category: '',
        allowedZones: [],
        isAlreadyRegistered: false,
        verificationToken: '',
        errorMessage: ''
    });

    // Barra Modo Demo
    const [showDemoTopBar, setShowDemoTopBar] = useState(true);
    const [demoBarOpen, setDemoBarOpen] = useState(false);
    const [demoTickets, setDemoTickets] = useState([]);
    const [loadingDemoTickets, setLoadingDemoTickets] = useState(false);

    // Formulario de 1 solo paso sin OTP
    const [formData, setFormData] = useState({
        ticketFolio: '',
        fullName: '',
        phone: '',
        email: '',
        verificationToken: ''
    });
    const [fieldErrors, setFieldErrors] = useState({});
    const [submitError, setSubmitError] = useState('');
    const [isRateLimited, setIsRateLimited] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [registrationNote, setRegistrationNote] = useState('');

    // Modal de recuperación de pase
    const [showRecoverModal, setShowRecoverModal] = useState(false);
    const [recoverForm, setRecoverForm] = useState({
        ticketFolio: '',
        contactQuery: ''
    });
    const [recoverLoading, setRecoverLoading] = useState(false);
    const [recoverError, setRecoverError] = useState('');

    // Listener de tamaño de pantalla para Confetti
    useEffect(() => {
        const handleResize = () => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight
            });
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Cargar configuración pública y boletos demo
    const fetchDemoTickets = useCallback(async () => {
        setLoadingDemoTickets(true);
        const res = await GetXfDemoSampleTickets();
        if (res.success && Array.isArray(res.tickets)) {
            setDemoTickets(res.tickets);
        }
        setLoadingDemoTickets(false);
    }, []);

    useEffect(() => {
        let mounted = true;
        const initConfig = async () => {
            const cfgRes = await GetXfPublicConfig();
            if (mounted && cfgRes.success && cfgRes.data) {
                setEventConfig(cfgRes.data);
            }
        };
        initConfig();
        fetchDemoTickets();
        return () => {
            mounted = false;
        };
    }, [fetchDemoTickets]);

    // Validar token QR si viene ?t= en la URL
    useEffect(() => {
        let mounted = true;

        const verifyUrlToken = async () => {
            if (!qrTokenParam) {
                setQrValidation({
                    checked: false,
                    isValid: false,
                    ticketFolio: '',
                    category: '',
                    allowedZones: [],
                    isAlreadyRegistered: false,
                    verificationToken: '',
                    errorMessage: ''
                });
                return;
            }

            setValidatingToken(true);
            setSubmitError('');
            setIsRateLimited(false);

            const res = await ValidateXfTicketToken(qrTokenParam, sessionId);
            if (!mounted) return;

            setValidatingToken(false);

            if (res.isRateLimited || res.status === 429) {
                setIsRateLimited(true);
                setSubmitError(
                    res.message ||
                        'Has excedido el límite de intentos permitidos. Por seguridad, espera 1 minuto antes de volver a intentar.'
                );
                return;
            }

            if (res.isValid && res.ticketFolio) {
                setQrValidation({
                    checked: true,
                    isValid: true,
                    ticketFolio: res.ticketFolio,
                    category: (res.category || 'GENERAL').toUpperCase(),
                    allowedZones: res.allowedZones || [],
                    isAlreadyRegistered: Boolean(res.isRegistered ?? res.isAlreadyRegistered),
                    verificationToken: res.verificationToken || qrTokenParam,
                    errorMessage: ''
                });
                setFormData((prev) => ({
                    ...prev,
                    ticketFolio: res.ticketFolio,
                    verificationToken: qrTokenParam
                }));
                setFieldErrors((prev) => ({ ...prev, ticketFolio: undefined }));
            } else {
                setQrValidation({
                    checked: true,
                    isValid: false,
                    ticketFolio: '',
                    category: '',
                    allowedZones: [],
                    isAlreadyRegistered: false,
                    verificationToken: '',
                    errorMessage:
                        '⚠️ El código QR escaneado es inválido o su firma de seguridad fue alterada. Ingresa tu folio manualmente o verifica tu boleto.'
                });
                setFormData((prev) => ({
                    ...prev,
                    ticketFolio: '',
                    verificationToken: ''
                }));
            }
        };

        verifyUrlToken();
        return () => {
            mounted = false;
        };
    }, [qrTokenParam, sessionId]);

    // Seleccionar un boleto desde la Barra de Modo Demo
    const handleSelectDemoTicket = (ticket) => {
        if (!ticket) return;
        const token = ticket.verificationToken || '';
        const folio = ticket.ticketFolio || '';

        // Si había sesión activa, limpiarla para probar el nuevo boleto
        if (activeSession) {
            ClearXfParticipantSession();
            setActiveSession(null);
        }

        setSubmitError('');
        setIsRateLimited(false);
        setFieldErrors({});

        if (token) {
            setSearchParams({ t: token });
        } else {
            setSearchParams({});
            setFormData((prev) => ({
                ...prev,
                ticketFolio: folio,
                verificationToken: ''
            }));
        }

        // Autocompletar datos sugeridos de prueba si los campos están vacíos
        setFormData((prev) => ({
            ...prev,
            ticketFolio: folio,
            verificationToken: token,
            fullName: prev.fullName || 'Carlos Xoloitzcuintle',
            phone: prev.phone || '6641234567',
            email: prev.email || `aficionado.${folio.toLowerCase().replace(/[^a-z0-9]/g, '')}@xolos.com.mx`
        }));
    };

    const handleSimulateTamperedQr = () => {
        if (activeSession) {
            ClearXfParticipantSession();
            setActiveSession(null);
        }
        setSearchParams({ t: 'WE9MTy0wMDAx.FIRMA_ALTERADA_999' });
    };

    const handleSimulateMassCampaign = () => {
        if (activeSession) {
            ClearXfParticipantSession();
            setActiveSession(null);
        }
        setSearchParams({ ref: 'print_mass' });
        setQrValidation({
            checked: false,
            isValid: false,
            ticketFolio: '',
            category: '',
            allowedZones: [],
            isAlreadyRegistered: false,
            verificationToken: '',
            errorMessage: ''
        });
        const firstAvailable = demoTickets.find((t) => !t.isRegistered) || demoTickets[0];
        setFormData((prev) => ({
            ...prev,
            ticketFolio: firstAvailable ? firstAvailable.ticketFolio : 'XOLO-0001',
            verificationToken: ''
        }));
    };

    const handleInputChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value
        }));
        if (fieldErrors[field]) {
            setFieldErrors((prev) => ({
                ...prev,
                [field]: undefined
            }));
        }
        if (submitError) {
            setSubmitError('');
        }
    };

    // Envío del formulario de 1 paso
    const handleRegisterSubmit = async (e) => {
        e.preventDefault();
        setSubmitError('');
        setIsRateLimited(false);
        setRegistrationNote('');

        const validation = ValidateXfRegistrationForm(formData);
        if (!validation.isValid) {
            setFieldErrors(validation.errors);
            return;
        }

        setFieldErrors({});
        setSubmitting(true);

        const payload = {
            ...validation.sanitizedData,
            verificationToken:
                qrValidation.isValid && qrValidation.verificationToken
                    ? qrValidation.verificationToken
                    : formData.verificationToken || null,
            referralSource: refParam || (qrValidation.isValid ? 'qr_individual' : 'direct')
        };

        const res = await RegisterXfParticipant(payload, sessionId);
        setSubmitting(false);

        if (res.isRateLimited || res.status === 429) {
            setIsRateLimited(true);
            setSubmitError(
                res.message ||
                    'Has excedido el límite de intentos permitidos. Por seguridad, espera 1 minuto antes de volver a intentar.'
            );
            return;
        }

        if (!res.success) {
            setSubmitError(
                res.message ||
                    'No fue posible completar el registro. Verifica los datos de tu boleto e intenta nuevamente.'
            );
            return;
        }

        const saved = SaveXfParticipantSession(res);
        setActiveSession(saved);
        if (res.isAutoRecovered) {
            setRegistrationNote('¡Bienvenido de vuelta! Hemos recuperado automáticamente tu registro oficial.');
        } else {
            setRegistrationNote('');
        }
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 7500);
        fetchDemoTickets();
    };

    // Envío del modal de recuperación de pase
    const handleRecoverSubmit = async (e) => {
        e.preventDefault();
        setRecoverError('');

        const cleanFolio = (recoverForm.ticketFolio || '').trim().toUpperCase();
        const cleanContact = (recoverForm.contactQuery || '').trim();

        if (!cleanFolio) {
            setRecoverError('Ingresa el folio de tu boleto (ej. XOLO-0001).');
            return;
        }
        if (!cleanContact || cleanContact.length < 5) {
            setRecoverError('Ingresa el teléfono de 10 dígitos o el correo con el que registraste tu boleto.');
            return;
        }

        setRecoverLoading(true);
        const res = await RecoverXfPass(
            {
                ticketFolio: cleanFolio,
                contactQuery: cleanContact
            },
            sessionId
        );
        setRecoverLoading(false);

        if (res.isRateLimited || res.status === 429) {
            setRecoverError(
                res.message ||
                    'Has excedido el límite de intentos permitidos. Por seguridad, espera 1 minuto antes de volver a intentar.'
            );
            return;
        }

        if (!res.success) {
            setRecoverError(
                res.message ||
                    'No encontramos un pase registrado con esa combinación de Folio y Teléfono/Correo.'
            );
            return;
        }

        const saved = SaveXfParticipantSession(res);
        setActiveSession(saved);
        setRegistrationNote('Tu pase digital e inscripción a la Tómbola Perrona han sido recuperados con éxito.');
        setShowRecoverModal(false);
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 6000);
    };

    // Limpiar sesión local para registrar otro boleto
    const handleResetForAnotherTicket = () => {
        ClearXfParticipantSession();
        setActiveSession(null);
        setShowConfetti(false);
        setRegistrationNote('');
        setSubmitError('');
        setIsRateLimited(false);
        setSearchParams({});
        setQrValidation({
            checked: false,
            isValid: false,
            ticketFolio: '',
            category: '',
            allowedZones: [],
            isAlreadyRegistered: false,
            verificationToken: '',
            errorMessage: ''
        });
        setFormData({
            ticketFolio: '',
            fullName: '',
            phone: '',
            email: '',
            verificationToken: ''
        });
        fetchDemoTickets();
    };

    // Boleto disponible por categoría para botones rápidos del Modo Demo
    const getDemoTicketByCategory = (cat) => {
        const available = demoTickets.find(
            (t) => (t.category || '').toUpperCase() === cat && !t.isRegistered
        );
        if (available) return available;
        return demoTickets.find((t) => (t.category || '').toUpperCase() === cat) || null;
    };

    const participant = activeSession?.participant || null;
    const categoryKey = (
        participant?.ticketCategory ||
        qrValidation.category ||
        'GENERAL'
    ).toUpperCase();
    const badgeTheme = CATEGORY_BADGE_STYLES[categoryKey] || CATEGORY_BADGE_STYLES.GENERAL;

    return (
        <div
            style={{
                minHeight: '100vh',
                background: "url('/img/page/backgrounds/Fondo-SelfieFan-Femenil.jpeg') center top / cover no-repeat fixed",
                color: '#f4f4f5',
                fontFamily: "'Montserrat', 'Inter', system-ui, sans-serif",
                paddingBottom: '3.5rem',
                position: 'relative',
                overflowX: 'hidden'
            }}
        >
            {showConfetti && (
                <Confetti
                    width={windowSize.width}
                    height={windowSize.height}
                    recycle={false}
                    numberOfPieces={320}
                    colors={['#dc2626', '#f59e0b', '#fbbf24', '#ffffff', '#991b1b']}
                    style={{ position: 'fixed', top: 0, left: 0, zIndex: 9999, pointerEvents: 'none' }}
                />
            )}

            {/* Barra Superior Colapsable: 🛠️ Modo Demo: Boletos de Prueba */}
            {showDemoTopBar ? (
                <div
                    style={{
                        background: 'rgba(15, 15, 20, 0.95)',
                        borderBottom: '1px solid rgba(245, 158, 11, 0.3)',
                        backdropFilter: 'blur(10px)',
                        position: 'sticky',
                        top: 0,
                        zIndex: 1020
                    }}
                >
                <div className="container" style={{ maxWidth: '560px' }}>
                    <div className="d-flex align-items-center justify-content-between py-2">
                        <button
                            type="button"
                            onClick={() => setDemoBarOpen((prev) => !prev)}
                            className="btn btn-sm d-flex align-items-center gap-2 text-start p-0 border-0"
                            style={{ color: '#fbbf24', fontWeight: 700, fontSize: '0.8rem' }}
                        >
                            <span>🛠️ Modo Demo: Boletos de Prueba</span>
                            <span
                                className="badge rounded-pill"
                                style={{
                                    background: 'rgba(245, 158, 11, 0.18)',
                                    color: '#fde68a',
                                    border: '1px solid rgba(245, 158, 11, 0.4)',
                                    fontSize: '0.68rem'
                                }}
                            >
                                {demoBarOpen ? 'Ocultar ▲' : 'Probar 1-Clic ▼'}
                            </span>
                        </button>

                        <div className="d-flex align-items-center gap-2">
                            <button
                                type="button"
                                onClick={() => navigate('/demo/xolofest/passport')}
                                className="btn btn-sm py-1 px-2"
                                style={{
                                    background: 'rgba(255,255,255,0.06)',
                                    border: '1px solid rgba(255,255,255,0.15)',
                                    color: '#e4e4e7',
                                    fontSize: '0.72rem',
                                    fontWeight: 600
                                }}
                            >
                                <i className="bi bi-passport me-1 text-warning"></i>
                                Mi Pasaporte
                            </button>
                            <button
                                type="button"
                                onClick={() => setShowDemoTopBar(false)}
                                className="btn btn-sm py-1 px-2"
                                style={{
                                    background: 'transparent',
                                    border: '1px solid rgba(255,255,255,0.2)',
                                    color: '#a1a1aa',
                                    fontSize: '0.72rem'
                                }}
                                title="Ocultar menú superior del demo"
                            >
                                <i className="bi bi-eye-slash"></i>
                            </button>
                        </div>
                    </div>

                    <AnimatePresence>
                        {demoBarOpen && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                style={{ overflow: 'hidden' }}
                            >
                                <div
                                    className="py-2 mb-2 px-3 rounded-3"
                                    style={{
                                        background: 'rgba(24, 24, 27, 0.9)',
                                        border: '1px solid rgba(245, 158, 11, 0.25)'
                                    }}
                                >
                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                        <small style={{ color: '#a1a1aa', fontSize: '0.73rem' }}>
                                            Autocompleta un boleto de prueba válido:
                                        </small>
                                        <button
                                            type="button"
                                            onClick={fetchDemoTickets}
                                            disabled={loadingDemoTickets}
                                            className="btn btn-link btn-sm p-0 text-decoration-none"
                                            style={{ color: '#fbbf24', fontSize: '0.72rem' }}
                                        >
                                            <i className="bi bi-arrow-clockwise me-1"></i>
                                            Refrescar
                                        </button>
                                    </div>

                                    <div className="d-flex flex-wrap gap-2 mb-2">
                                        {['GENERAL', 'VIP', 'CANCHA'].map((cat) => {
                                            const sample = getDemoTicketByCategory(cat);
                                            return (
                                                <button
                                                    key={cat}
                                                    type="button"
                                                    disabled={!sample}
                                                    onClick={() => handleSelectDemoTicket(sample)}
                                                    className="btn btn-sm flex-grow-1"
                                                    style={{
                                                        background:
                                                            cat === 'VIP'
                                                                ? 'rgba(245, 158, 11, 0.16)'
                                                                : cat === 'CANCHA'
                                                                ? 'rgba(220, 38, 38, 0.2)'
                                                                : 'rgba(255, 255, 255, 0.08)',
                                                        border:
                                                            cat === 'VIP'
                                                                ? '1px solid #f59e0b'
                                                                : cat === 'CANCHA'
                                                                ? '1px solid #dc2626'
                                                                : '1px solid #52525b',
                                                        color: '#ffffff',
                                                        fontSize: '0.73rem',
                                                        fontWeight: 700
                                                    }}
                                                >
                                                    <i className="bi bi-qr-code-scan me-1 text-warning"></i>
                                                    {cat}: {sample ? sample.ticketFolio : 'Sin stock'}
                                                </button>
                                            );
                                        })}
                                    </div>

                                    <div className="d-flex flex-wrap gap-2 pt-1 border-top border-secondary border-opacity-25">
                                        <button
                                            type="button"
                                            onClick={handleSimulateMassCampaign}
                                            className="btn btn-sm"
                                            style={{
                                                background: 'rgba(255,255,255,0.05)',
                                                border: '1px solid rgba(255,255,255,0.15)',
                                                color: '#d4d4d8',
                                                fontSize: '0.7rem'
                                            }}
                                        >
                                            <i className="bi bi-Input-cursor-text me-1"></i>
                                            Simular QR Masivo (?ref=print_mass)
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleSimulateTamperedQr}
                                            className="btn btn-sm"
                                            style={{
                                                background: 'rgba(220, 38, 38, 0.12)',
                                                border: '1px solid rgba(220, 38, 38, 0.4)',
                                                color: '#fca5a5',
                                                fontSize: '0.7rem'
                                            }}
                                        >
                                            <i className="bi bi-shield-exclamation me-1"></i>
                                            Simular QR Alterado
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>
            ) : (
                <button
                    type="button"
                    onClick={() => setShowDemoTopBar(true)}
                    className="btn btn-sm rounded-pill position-fixed top-0 end-0 m-2 px-3 py-1 shadow-lg"
                    style={{
                        zIndex: 1040,
                        background: 'rgba(24, 24, 27, 0.92)',
                        border: '1px solid rgba(245, 158, 11, 0.6)',
                        color: '#fde68a',
                        fontSize: '0.72rem',
                        backdropFilter: 'blur(8px)'
                    }}
                    title="Mostrar menú superior del demo"
                >
                    <i className="bi bi-tools me-1 text-warning"></i>
                    Modo Demo
                </button>
            )}

            {/* Contenedor Principal Mobile-First */}
            <div className="container pt-4" style={{ maxWidth: '540px' }}>
                {/* Hero Header Identidad Club Tijuana Xoloitzcuintles */}
                <motion.div
                    initial={{ opacity: 0, y: -12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    className="text-center mb-4"
                >
                    <div
                        className="d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill mb-3"
                        style={{
                            background: 'linear-gradient(90deg, rgba(220,38,38,0.22) 0%, rgba(245,158,11,0.22) 100%)',
                            border: '1px solid rgba(245, 158, 11, 0.45)',
                            boxShadow: '0 0 20px rgba(220, 38, 38, 0.25)'
                        }}
                    >
                        <span style={{ color: '#f59e0b', fontSize: '0.8rem', fontWeight: 800, letterSpacing: '1px' }}>
                            ★ CLUB TIJUANA XOLOITZCUINTLES DE CALIENTE ★
                        </span>
                    </div>

                    <h1
                        className="fw-black text-uppercase mb-2"
                        style={{
                            fontSize: 'clamp(1.75rem, 6vw, 2.35rem)',
                            fontWeight: 900,
                            letterSpacing: '-0.5px',
                            lineHeight: 1.1,
                            background: 'linear-gradient(180deg, #ffffff 0%, #fde68a 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent'
                        }}
                    >
                        {eventConfig?.eventName || 'XOLO FEST 2026'}
                    </h1>

                    <p
                        className="mb-0 mx-auto"
                        style={{
                            color: '#d4d4d8',
                            fontSize: '0.94rem',
                            maxWidth: '440px',
                            lineHeight: 1.45
                        }}
                    >
                        Registra tu boleto oficial, obtén tu folio para la gran{' '}
                        <strong style={{ color: '#f59e0b' }}>Tómbola Perrona</strong> y activa tu{' '}
                        <strong style={{ color: '#ffffff' }}>Pasaporte Digital</strong>.
                    </p>
                </motion.div>

                {/* VISTA 1: TARJETA TRIUNFAL OFICIAL SI YA HAY SESIÓN ACTIVA */}
                {participant ? (
                    <motion.div
                        key="triumphal-card"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.4 }}
                        className="rounded-4 p-4 position-relative overflow-hidden"
                        style={{
                            background:
                                'linear-gradient(165deg, rgba(24, 24, 27, 0.96) 0%, rgba(9, 9, 11, 0.98) 65%, rgba(69, 10, 10, 0.9) 100%)',
                            border: '2px solid #f59e0b',
                            boxShadow: '0 20px 50px rgba(0, 0, 0, 0.8), 0 0 30px rgba(245, 158, 11, 0.2)'
                        }}
                    >
                        {/* Cinta superior de estado */}
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                            <span
                                className="badge px-3 py-2 rounded-pill"
                                style={{
                                    background: 'rgba(16, 185, 129, 0.18)',
                                    border: '1px solid #10b981',
                                    color: '#6ee7b7',
                                    fontSize: '0.74rem',
                                    fontWeight: 800,
                                    letterSpacing: '0.5px'
                                }}
                            >
                                <i className="bi bi-patch-check-fill me-1"></i>
                                REGISTRO OFICIAL CONFIRMADO
                            </span>

                            <span
                                className="badge px-3 py-2 rounded-pill"
                                style={{
                                    background: badgeTheme.bg,
                                    color: badgeTheme.text,
                                    border: `1px solid ${badgeTheme.border}`,
                                    fontSize: '0.75rem',
                                    fontWeight: 800
                                }}
                            >
                                CATEGORÍA: {participant.ticketCategory || 'GENERAL'}
                            </span>
                        </div>

                        {registrationNote && (
                            <div
                                className="alert py-2 px-3 mb-3 rounded-3 d-flex align-items-center gap-2"
                                style={{
                                    background: 'rgba(245, 158, 11, 0.14)',
                                    border: '1px solid rgba(245, 158, 11, 0.45)',
                                    color: '#fde68a',
                                    fontSize: '0.82rem'
                                }}
                            >
                                <i className="bi bi-stars fs-5 text-warning"></i>
                                <span>{registrationNote}</span>
                            </div>
                        )}

                        {/* Banner Dorado del Folio de Sorteo */}
                        <div
                            className="p-3 rounded-4 mb-4 text-center"
                            style={{
                                background:
                                    'linear-gradient(135deg, rgba(245, 158, 11, 0.22) 0%, rgba(220, 38, 38, 0.25) 100%)',
                                border: '1px solid rgba(245, 158, 11, 0.6)'
                            }}
                        >
                            <div
                                className="text-uppercase fw-bold mb-1"
                                style={{ color: '#fde68a', fontSize: '0.73rem', letterSpacing: '1.5px' }}
                            >
                                🎰 FOLIO OFICIAL TÓMBOLA PERRONA
                            </div>
                            <div
                                style={{
                                    fontFamily: "'Courier New', monospace",
                                    fontSize: '1.85rem',
                                    fontWeight: 900,
                                    color: '#ffffff',
                                    letterSpacing: '2px',
                                    textShadow: '0 2px 10px rgba(245, 158, 11, 0.45)'
                                }}
                            >
                                #{participant.raffleTicketCode}
                            </div>
                            <div style={{ color: '#d4d4d8', fontSize: '0.78rem' }}>
                                Boleto Vinculado: <strong className="text-white">{participant.ticketFolio}</strong>
                            </div>
                        </div>

                        {/* Código QR Oficial para presentar en puertas del estadio */}
                        <div className="text-center mb-4">
                            <div
                                className="d-inline-block p-3 rounded-4"
                                style={{
                                    background: '#ffffff',
                                    border: '4px solid #f59e0b',
                                    boxShadow: '0 10px 30px rgba(0,0,0,0.5)'
                                }}
                            >
                                <QRCodeSVG
                                    value={
                                        participant.verificationToken ||
                                        activeSession?.participantToken ||
                                        participant.ticketFolio
                                    }
                                    size={200}
                                    bgColor="#ffffff"
                                    fgColor="#09090b"
                                    level="H"
                                    includeMargin={true}
                                    imageSettings={{
                                        src: '/img/favicon/Big.png',
                                        x: undefined,
                                        y: undefined,
                                        height: 44,
                                        width: 44,
                                        excavate: true
                                    }}
                                />
                            </div>
                        </div>

                        {/* Resumen de Zonas Incluidas */}
                        {Array.isArray(participant.allowedZones) && participant.allowedZones.length > 0 && (
                            <div className="mb-4 text-center">
                                <div
                                    className="text-uppercase mb-2"
                                    style={{ color: '#a1a1aa', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px' }}
                                >
                                    Zonas Autorizadas en tu Boleto
                                </div>
                                <div className="d-flex flex-wrap justify-content-center gap-2">
                                    {participant.allowedZones.map((zone) => (
                                        <span
                                            key={zone}
                                            className="badge px-2 py-1"
                                            style={{
                                                background: 'rgba(220, 38, 38, 0.18)',
                                                border: '1px solid rgba(220, 38, 38, 0.5)',
                                                color: '#fecaca',
                                                fontSize: '0.72rem'
                                            }}
                                        >
                                            <i className="bi bi-geo-alt-fill me-1 text-danger"></i>
                                            {zone}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Mensaje Triunfal Oficial de la Tómbola Perrona */}
                        <div
                            className="p-3 rounded-3 mb-4 text-center"
                            style={{
                                background: 'rgba(255, 255, 255, 0.04)',
                                border: '1px solid rgba(245, 158, 11, 0.3)'
                            }}
                        >
                            <p className="mb-0" style={{ fontSize: '0.96rem', lineHeight: 1.55, color: '#f4f4f5' }}>
                                ¡Felicidades, <strong style={{ color: '#fde68a' }}>{participant.fullName}</strong>! Tu
                                boleto ha sido registrado con éxito. Ya estás participando en la gran{' '}
                                <strong style={{ color: '#f59e0b' }}>Tómbola Perrona</strong> con el folio{' '}
                                <strong
                                    style={{
                                        color: '#ffffff',
                                        background: '#dc2626',
                                        padding: '2px 8px',
                                        borderRadius: '6px',
                                        fontFamily: 'monospace'
                                    }}
                                >
                                    #{participant.raffleTicketCode}
                                </strong>
                                . Guarda este pase y preséntalo en las puertas del estadio el día del evento para
                                activar tu recorrido.
                            </p>
                        </div>

                        {/* Botón Principal hacia el Pasaporte Digital y Secundario para pruebas */}
                        <div className="d-grid gap-2">
                            <button
                                type="button"
                                onClick={() => navigate('/demo/xolofest/passport')}
                                className="btn btn-lg fw-bold py-3 rounded-3"
                                style={{
                                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                    color: '#09090b',
                                    border: 'none',
                                    boxShadow: '0 8px 24px rgba(245, 158, 11, 0.35)',
                                    fontSize: '1rem'
                                }}
                            >
                                🛂 Ver mi Pasaporte Digital
                            </button>

                            <button
                                type="button"
                                onClick={handleResetForAnotherTicket}
                                className="btn btn-sm py-2 mt-1"
                                style={{
                                    background: 'transparent',
                                    border: '1px solid rgba(255,255,255,0.2)',
                                    color: '#a1a1aa',
                                    fontSize: '0.82rem'
                                }}
                            >
                                <i className="bi bi-arrow-repeat me-1"></i>
                                Registrar otro boleto
                            </button>
                        </div>
                    </motion.div>
                ) : (
                    /* VISTA 2: FORMULARIO DE PRE-REGISTRO DE 1 SOLO PASO SIN OTP */
                    <motion.div
                        key="registration-form"
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.35 }}
                        className="rounded-4 p-4"
                        style={{
                            background: 'rgba(24, 24, 27, 0.92)',
                            border: '1px solid rgba(220, 38, 38, 0.4)',
                            boxShadow: '0 20px 45px rgba(0, 0, 0, 0.75)'
                        }}
                    >
                        {/* Estado de validación del QR */}
                        {validatingToken && (
                            <div
                                className="alert py-3 px-3 mb-3 rounded-3 d-flex align-items-center gap-3"
                                style={{
                                    background: 'rgba(245, 158, 11, 0.12)',
                                    border: '1px solid rgba(245, 158, 11, 0.4)',
                                    color: '#fde68a'
                                }}
                            >
                                <div className="spinner-border spinner-border-sm text-warning" role="status" />
                                <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                                    Verificando firma criptográfica de tu código QR...
                                </span>
                            </div>
                        )}

                        {/* Alerta de QR Inválido o Adulterado */}
                        {!validatingToken && qrValidation.checked && !qrValidation.isValid && (
                            <div
                                role="alert"
                                className="alert py-3 px-3 mb-4 rounded-3"
                                style={{
                                    background: 'rgba(220, 38, 38, 0.18)',
                                    border: '2px solid #dc2626',
                                    color: '#fecaca',
                                    fontSize: '0.86rem',
                                    fontWeight: 600
                                }}
                            >
                                {qrValidation.errorMessage ||
                                    '⚠️ El código QR escaneado es inválido o su firma de seguridad fue alterada. Ingresa tu folio manualmente o verifica tu boleto.'}
                            </div>
                        )}

                        {/* Insignia de Boleto Autenticado por QR */}
                        {!validatingToken && qrValidation.checked && qrValidation.isValid && (
                            <div
                                className="p-3 mb-4 rounded-3"
                                style={{
                                    background:
                                        'linear-gradient(135deg, rgba(16, 185, 129, 0.16) 0%, rgba(245, 158, 11, 0.16) 100%)',
                                    border: '1px solid #f59e0b'
                                }}
                            >
                                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                                    <span
                                        style={{
                                            color: '#fde68a',
                                            fontWeight: 800,
                                            fontSize: '0.82rem',
                                            letterSpacing: '0.4px'
                                        }}
                                    >
                                        🔒 BOLETO AUTENTICADO POR QR — CATEGORÍA: {qrValidation.category}
                                    </span>
                                    <span
                                        className="badge"
                                        style={{
                                            background: '#10b981',
                                            color: '#052e16',
                                            fontWeight: 800,
                                            fontSize: '0.7rem'
                                        }}
                                    >
                                        BOLETO VÁLIDO
                                    </span>
                                </div>
                                {qrValidation.isAlreadyRegistered && (
                                    <div className="mt-2 pt-2 border-top border-warning border-opacity-25">
                                        <small style={{ color: '#fcd34d', fontSize: '0.76rem' }}>
                                            <i className="bi bi-info-circle-fill me-1"></i>
                                            Este boleto ya fue registrado. Si eres el titular, ingresa tus mismos datos
                                            o usa el botón de recuperar pase abajo.
                                        </small>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Indicador de Modo Campaña Masiva (?ref=print_mass) */}
                        {!qrTokenParam && refParam === 'print_mass' && (
                            <div
                                className="p-2 px-3 mb-3 rounded-3 d-flex align-items-center gap-2"
                                style={{
                                    background: 'rgba(255, 255, 255, 0.05)',
                                    border: '1px solid rgba(255, 255, 255, 0.15)',
                                    color: '#d4d4d8',
                                    fontSize: '0.78rem'
                                }}
                            >
                                <i className="bi bi-ticket-perforated-fill text-warning"></i>
                                <span>
                                    Acceso por QR de campaña impresa: escribe manualmente el folio impreso en tu boleto.
                                </span>
                            </div>
                        )}

                        {/* Alerta de Error General o Rate Limit HTTP 429 */}
                        {submitError && (
                            <div
                                role="alert"
                                className="alert py-3 px-3 mb-4 rounded-3 d-flex align-items-start gap-2"
                                style={{
                                    background: isRateLimited
                                        ? 'rgba(245, 158, 11, 0.16)'
                                        : 'rgba(220, 38, 38, 0.18)',
                                    border: isRateLimited ? '2px solid #f59e0b' : '1px solid #dc2626',
                                    color: isRateLimited ? '#fde68a' : '#fecaca',
                                    fontSize: '0.85rem'
                                }}
                            >
                                <i
                                    className={`bi ${
                                        isRateLimited ? 'bi-hourglass-split text-warning' : 'bi-exclamation-triangle-fill text-danger'
                                    } fs-5`}
                                ></i>
                                <div>
                                    {isRateLimited && (
                                        <div className="fw-bold text-uppercase mb-1" style={{ fontSize: '0.75rem' }}>
                                            Protección Anti-Fuerza Bruta Activada (HTTP 429)
                                        </div>
                                    )}
                                    <span>{submitError}</span>
                                </div>
                            </div>
                        )}

                        <form onSubmit={handleRegisterSubmit} noValidate>
                            {/* Campo 1: Folio de Boleto */}
                            <div className="mb-3">
                                <label
                                    htmlFor="xf-ticket-folio"
                                    className="form-label fw-bold text-uppercase mb-1"
                                    style={{ fontSize: '0.76rem', color: '#e4e4e7', letterSpacing: '0.5px' }}
                                >
                                    1. Folio de Boleto <span className="text-danger">*</span>
                                </label>
                                <div className="input-group">
                                    <span
                                        className="input-group-text border-0"
                                        style={{
                                            background: '#27272a',
                                            color: qrValidation.isValid ? '#f59e0b' : '#a1a1aa'
                                        }}
                                    >
                                        <i
                                            className={`bi ${
                                                qrValidation.isValid ? 'bi-lock-fill' : 'bi-ticket-detailed-fill'
                                            }`}
                                        ></i>
                                    </span>
                                    <input
                                        id="xf-ticket-folio"
                                        type="text"
                                        className="form-control form-control-lg"
                                        placeholder="Ej. XOLO-0042"
                                        value={formData.ticketFolio}
                                        readOnly={qrValidation.isValid}
                                        disabled={qrValidation.isValid || submitting}
                                        onChange={(e) =>
                                            handleInputChange('ticketFolio', e.target.value.toUpperCase())
                                        }
                                        style={{
                                            background: qrValidation.isValid ? '#18181b' : '#09090b',
                                            border: fieldErrors.ticketFolio
                                                ? '1px solid #dc2626'
                                                : qrValidation.isValid
                                                ? '1px solid #f59e0b'
                                                : '1px solid #3f3f46',
                                            color: qrValidation.isValid ? '#fde68a' : '#ffffff',
                                            fontWeight: 800,
                                            fontFamily: 'monospace',
                                            letterSpacing: '1px'
                                        }}
                                    />
                                </div>
                                {fieldErrors.ticketFolio && (
                                    <small className="text-danger d-block mt-1" style={{ fontSize: '0.76rem' }}>
                                        {fieldErrors.ticketFolio}
                                    </small>
                                )}
                            </div>

                            {/* Campo 2: Nombre Completo */}
                            <div className="mb-3">
                                <label
                                    htmlFor="xf-full-name"
                                    className="form-label fw-bold text-uppercase mb-1"
                                    style={{ fontSize: '0.76rem', color: '#e4e4e7', letterSpacing: '0.5px' }}
                                >
                                    2. Nombre Completo <span className="text-danger">*</span>
                                </label>
                                <input
                                    id="xf-full-name"
                                    type="text"
                                    className="form-control form-control-lg"
                                    placeholder="Nombre y Apellidos"
                                    value={formData.fullName}
                                    disabled={submitting}
                                    onChange={(e) => handleInputChange('fullName', e.target.value)}
                                    style={{
                                        background: '#09090b',
                                        border: fieldErrors.fullName ? '1px solid #dc2626' : '1px solid #3f3f46',
                                        color: '#ffffff',
                                        fontSize: '0.95rem'
                                    }}
                                />
                                {fieldErrors.fullName && (
                                    <small className="text-danger d-block mt-1" style={{ fontSize: '0.76rem' }}>
                                        {fieldErrors.fullName}
                                    </small>
                                )}
                            </div>

                            {/* Campo 3: Teléfono / WhatsApp (10 dígitos) */}
                            <div className="mb-3">
                                <label
                                    htmlFor="xf-phone"
                                    className="form-label fw-bold text-uppercase mb-1"
                                    style={{ fontSize: '0.76rem', color: '#e4e4e7', letterSpacing: '0.5px' }}
                                >
                                    3. Teléfono / WhatsApp (10 dígitos) <span className="text-danger">*</span>
                                </label>
                                <input
                                    id="xf-phone"
                                    type="tel"
                                    inputMode="numeric"
                                    maxLength={14}
                                    className="form-control form-control-lg"
                                    placeholder="Ej. 6641234567"
                                    value={formData.phone}
                                    disabled={submitting}
                                    onChange={(e) => handleInputChange('phone', e.target.value)}
                                    style={{
                                        background: '#09090b',
                                        border: fieldErrors.phone ? '1px solid #dc2626' : '1px solid #3f3f46',
                                        color: '#ffffff',
                                        fontSize: '0.95rem'
                                    }}
                                />
                                {fieldErrors.phone && (
                                    <small className="text-danger d-block mt-1" style={{ fontSize: '0.76rem' }}>
                                        {fieldErrors.phone}
                                    </small>
                                )}
                            </div>

                            {/* Campo 4: Correo Electrónico */}
                            <div className="mb-4">
                                <label
                                    htmlFor="xf-email"
                                    className="form-label fw-bold text-uppercase mb-1"
                                    style={{ fontSize: '0.76rem', color: '#e4e4e7', letterSpacing: '0.5px' }}
                                >
                                    4. Correo Electrónico <span className="text-danger">*</span>
                                </label>
                                <input
                                    id="xf-email"
                                    type="email"
                                    className="form-control form-control-lg"
                                    placeholder="tu.correo@ejemplo.com"
                                    value={formData.email}
                                    disabled={submitting}
                                    onChange={(e) => handleInputChange('email', e.target.value)}
                                    style={{
                                        background: '#09090b',
                                        border: fieldErrors.email ? '1px solid #dc2626' : '1px solid #3f3f46',
                                        color: '#ffffff',
                                        fontSize: '0.95rem'
                                    }}
                                />
                                {fieldErrors.email && (
                                    <small className="text-danger d-block mt-1" style={{ fontSize: '0.76rem' }}>
                                        {fieldErrors.email}
                                    </small>
                                )}
                            </div>

                            {/* Botón Principal de Registro */}
                            <div className="d-grid mb-3">
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="btn btn-lg fw-bold py-3 rounded-3"
                                    style={{
                                        background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
                                        border: '1px solid #f59e0b',
                                        color: '#ffffff',
                                        fontSize: '1.02rem',
                                        boxShadow: '0 10px 25px rgba(220, 38, 38, 0.4)'
                                    }}
                                >
                                    {submitting ? (
                                        <>
                                            <span
                                                className="spinner-border spinner-border-sm me-2"
                                                role="status"
                                                aria-hidden="true"
                                            ></span>
                                            Validando y Asignando Folio...
                                        </>
                                    ) : (
                                        'Registrarme ahora'
                                    )}
                                </button>
                            </div>

                            {/* Enlace / Botón Secundario de Recuperación de Pase */}
                            <div className="text-center pt-2 border-top border-secondary border-opacity-25">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setRecoverError('');
                                        setRecoverForm({
                                            ticketFolio: formData.ticketFolio || '',
                                            contactQuery: ''
                                        });
                                        setShowRecoverModal(true);
                                    }}
                                    className="btn btn-link text-decoration-none"
                                    style={{ color: '#fbbf24', fontSize: '0.86rem', fontWeight: 700 }}
                                >
                                    <i className="bi bi-key-fill me-1"></i>
                                    ¿Ya registraste tu boleto? Recupera tu pase aquí
                                </button>
                            </div>
                        </form>
                    </motion.div>
                )}
            </div>

            {/* Modal Limpio de Recuperación de Pase (Folio + Teléfono o Correo) */}
            <AnimatePresence>
                {showRecoverModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center p-3"
                        style={{
                            background: 'rgba(0, 0, 0, 0.82)',
                            backdropFilter: 'blur(6px)',
                            zIndex: 1055
                        }}
                    >
                        <motion.div
                            initial={{ scale: 0.92, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.92, opacity: 0 }}
                            className="rounded-4 p-4 w-100"
                            style={{
                                maxWidth: '440px',
                                background: '#18181b',
                                border: '1px solid #f59e0b',
                                boxShadow: '0 25px 50px rgba(0,0,0,0.85)'
                            }}
                        >
                            <div className="d-flex align-items-center justify-content-between mb-3">
                                <h5 className="mb-0 fw-bold text-white">
                                    <i className="bi bi-shield-check text-warning me-2"></i>
                                    Recuperar mi Pase Digital
                                </h5>
                                <button
                                    type="button"
                                    onClick={() => setShowRecoverModal(false)}
                                    className="btn btn-sm text-secondary border-0"
                                >
                                    <i className="bi bi-x-lg text-white"></i>
                                </button>
                            </div>

                            <p style={{ color: '#a1a1aa', fontSize: '0.84rem' }}>
                                Ingresa el folio de tu boleto y el teléfono (10 dígitos) o correo electrónico con el
                                que realizaste tu registro.
                            </p>

                            {recoverError && (
                                <div
                                    className="alert py-2 px-3 mb-3 rounded-3"
                                    style={{
                                        background: 'rgba(220, 38, 38, 0.18)',
                                        border: '1px solid #dc2626',
                                        color: '#fecaca',
                                        fontSize: '0.82rem'
                                    }}
                                >
                                    {recoverError}
                                </div>
                            )}

                            <form onSubmit={handleRecoverSubmit}>
                                <div className="mb-3">
                                    <label
                                        className="form-label fw-bold text-uppercase mb-1"
                                        style={{ fontSize: '0.74rem', color: '#e4e4e7' }}
                                    >
                                        Folio de Boleto
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Ej. XOLO-0001"
                                        value={recoverForm.ticketFolio}
                                        onChange={(e) =>
                                            setRecoverForm((prev) => ({
                                                ...prev,
                                                ticketFolio: e.target.value.toUpperCase()
                                            }))
                                        }
                                        style={{
                                            background: '#09090b',
                                            border: '1px solid #3f3f46',
                                            color: '#ffffff',
                                            fontFamily: 'monospace',
                                            fontWeight: 700
                                        }}
                                    />
                                </div>

                                <div className="mb-4">
                                    <label
                                        className="form-label fw-bold text-uppercase mb-1"
                                        style={{ fontSize: '0.74rem', color: '#e4e4e7' }}
                                    >
                                        Teléfono (10 dígitos) o Correo Electrónico
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="6641234567 o correo@dominio.com"
                                        value={recoverForm.contactQuery}
                                        onChange={(e) =>
                                            setRecoverForm((prev) => ({
                                                ...prev,
                                                contactQuery: e.target.value
                                            }))
                                        }
                                        style={{
                                            background: '#09090b',
                                            border: '1px solid #3f3f46',
                                            color: '#ffffff'
                                        }}
                                    />
                                </div>

                                <div className="d-flex gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setShowRecoverModal(false)}
                                        className="btn flex-grow-1"
                                        style={{
                                            background: 'rgba(255,255,255,0.06)',
                                            color: '#d4d4d8',
                                            border: '1px solid rgba(255,255,255,0.15)'
                                        }}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={recoverLoading}
                                        className="btn fw-bold flex-grow-1"
                                        style={{
                                            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                            color: '#09090b',
                                            border: 'none'
                                        }}
                                    >
                                        {recoverLoading ? 'Consultando...' : 'Recuperar mi Pase'}
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default XoloFestLanding;
